import { useFlagsWithoutEvaluation } from '@iheartjane/feature-flags';
import type { ToastsRenderer } from '@iheartjane/reefer';
import { ToastProvider } from '@iheartjane/reefer';
import { featureFlagProperties } from '@iheartjane/tracking';
import type { PropsWithChildren, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Outlet, ScrollRestoration, useLocation } from 'react-router';

import type { AlgoliaProduct } from '@jane/search/types';

import type { RootLoaderType } from '../../data-access';
import { useKioskAeropayUser, useKioskUser } from '../../data-access';
import { useThemeGenerator, useTypedRouteLoaderData } from '../../hooks';
import {
  register,
  registerUser,
  rewriteManifest,
  trackingPath,
} from '../../util';
import { BrandSpecialsProvider } from './BrandSpecialsProvider/BrandSpecialsProvider';
import { CartInfoForTrackingProvider } from './CartInfoForTrackingProvider';
import { ComplianceLanguageProvider } from './ComplianceLanguageProvider';
import { IdleProvider } from './IdleProvider';
import { JaneModeProvider } from './JaneModeProvider';
import { KioskThemeProvider } from './KioskThemeProvider';
import { ModalsDisplayedProvider } from './ModalsDisplayedProvider';
import { ProductTrackingProvider } from './ProductTrackingProvider';
import { SearchProvider } from './SearchProvider';
import { SessionProvider } from './SessionProvider';

interface AppProviderProps {
  renderToasts: (props: ToastsRenderer) => ReactNode;
}

export const AppProvider = ({
  children,
  renderToasts,
}: PropsWithChildren<AppProviderProps>) => {
  const signedIn = useRef(false);
  const { store } = useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: kioskUser } = useKioskUser();
  const { data: kioskAeropayUser } = useKioskAeropayUser({
    janeUserId: kioskUser?.id,
  });
  const theme = useThemeGenerator(store.custom_theme_color ?? '#5E1FF0');
  const location = useLocation();

  const featureFlags = useFlagsWithoutEvaluation();
  const mappedFlags = useMemo(() => {
    return featureFlagProperties(featureFlags);
  }, [featureFlags]);

  const setEventSuperProperties = useCallback(() => {
    register({
      'Store ID': `${store.id}`,
      'Store Name': store.name,
      'Store Type': [store.recreational ? 'rec' : 'med'],
      'User Segments': kioskUser?.user_segments.length
        ? kioskUser?.user_segments.join(',')
        : null,
      'Feature Flags': { ...mappedFlags },
    });
  }, [store, kioskUser?.user_segments, mappedFlags]);

  useEffect(() => {
    setEventSuperProperties();
  }, [setEventSuperProperties]);

  useEffect(() => {
    register({
      'Path URL': trackingPath(location.pathname, location.search),
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    rewriteManifest(store.name);
  }, [store]);

  useEffect(() => {
    if (kioskUser && !signedIn.current) {
      signedIn.current = true;
      janeMixpanel.identify(kioskUser.id.toString());

      registerUser({
        'First Name': kioskUser.first_name,
        'Last Name': kioskUser.last_name,
        Email: kioskUser.email,
        Phone: kioskUser.phone,
        'User ID': Number(kioskUser.id),
        'Feature Flags': { ...mappedFlags },
      });
    }
    // This should only fire if a user logs out.
    if (!kioskUser && signedIn.current) {
      signedIn.current = false;
      janeMixpanel.reset();

      setEventSuperProperties();
    }
  }, [kioskUser]);

  useEffect(() => {
    if (kioskAeropayUser) {
      const verified = !!kioskAeropayUser.bank_accounts.length;
      register({
        'Aeropay Account Verified': verified,
      });
      registerUser({
        'Aeropay Account Verified': verified,
      });
      if (kioskAeropayUser.bank_accounts.length === 1) {
        janeMixpanel.people.set_once({
          aeropayLinkDate: new Date(),
        });
      }
    } else {
      register({
        'Aeropay Account Verified': false,
      });
    }
  }, [kioskAeropayUser]);

  /**
   * Ensures the theme provider re-renders when changing between stores.
   */
  const appKey = useMemo(() => store.id, [store.id]);

  return (
    <KioskThemeProvider key={appKey} theme={theme}>
      <SessionProvider>
        <SearchProvider<AlgoliaProduct> indexPrefix="menu-products-">
          <ModalsDisplayedProvider>
            <ProductTrackingProvider>
              <IdleProvider>
                <ToastProvider render={renderToasts}>
                  <JaneModeProvider isJaneMode={!store.custom_theme_color}>
                    <CartInfoForTrackingProvider>
                      <BrandSpecialsProvider>
                        <ComplianceLanguageProvider
                          languageSettings={
                            store.store_compliance_language_settings
                          }
                        >
                          {children}
                          <Outlet />
                          <ScrollRestoration />
                        </ComplianceLanguageProvider>
                      </BrandSpecialsProvider>
                    </CartInfoForTrackingProvider>
                  </JaneModeProvider>
                </ToastProvider>
              </IdleProvider>
            </ProductTrackingProvider>
          </ModalsDisplayedProvider>
        </SearchProvider>
      </SessionProvider>
    </KioskThemeProvider>
  );
};
